import { Link } from "react-router-dom";
import { ArrowRight, Zap, Shield, Globe } from "lucide-react";
import { SeoHead } from "../../seo/SeoHead";
import { buildWebSiteJsonLd, buildBreadcrumbListJsonLd } from "../../seo/site";

const SEO_TITLE = "Raguia vs ChatGPT Enterprise — Quelle IA pour votre PME française ?";
const SEO_DESCRIPTION =
  "Comparez Raguia et ChatGPT Enterprise. Découvrez pourquoi Raguia est l'alternative souveraine, sécurisée et abordable pour les PME françaises.";

export default function VsChatgptEnterprisePage() {
  const jsonLd = [
    buildWebSiteJsonLd(),
    buildBreadcrumbListJsonLd([
      { name: "Accueil", path: "/" },
      { name: "Comparatif", path: "/comparatif" },
      { name: "Raguia vs ChatGPT Enterprise", path: "/vs/chatgpt-enterprise" },
    ]),
  ];

  return (
    <div className="bg-stone-50 font-body text-[#010101] selection:bg-[#A43032] selection:text-white min-h-screen pb-20">
      <SeoHead
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        jsonLd={jsonLd}
        path="/vs/chatgpt-enterprise"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 lg:px-8 max-w-7xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-[#A43032] text-sm font-medium mb-6">
          <Zap className="size-4" /> Comparatif Solutions IA B2B
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold tracking-tight mb-6">
          Raguia vs <span className="text-stone-500">ChatGPT Enterprise</span>
        </h1>
        <p className="text-xl text-stone-600 max-w-3xl mx-auto mb-10 leading-relaxed">
          ChatGPT Enterprise est puissant, mais souvent trop complexe, coûteux et basé aux États-Unis pour les PME. Si vous cherchez une IA documentaire souveraine, avec un budget maîtrisé et hébergée en France, <strong className="text-stone-900">Raguia est l'alternative idéale.</strong>
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/contact"
            className="w-full sm:w-auto px-8 py-4 bg-[#A43032] text-white rounded-xl font-semibold shadow-lg shadow-red-900/20 hover:bg-red-800 transition-all flex items-center justify-center gap-2"
          >
            Démarrer avec Raguia <ArrowRight className="size-5" />
          </Link>
          <Link
            to="/comparatif"
            className="w-full sm:w-auto px-8 py-4 bg-white text-stone-700 border border-stone-200 rounded-xl font-semibold hover:bg-stone-50 hover:border-stone-300 transition-all text-center"
          >
            Voir tous les comparatifs
          </Link>
        </div>
      </section>

      {/* Table Section */}
      <section className="py-16 px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="bg-white rounded-3xl shadow-sm border border-stone-200 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-stone-200">
            <div className="p-8 hidden md:block bg-stone-50">
              <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">Critères</h3>
              <p className="text-sm text-stone-500">Ce qui compte vraiment pour votre PME.</p>
            </div>
            <div className="p-8 bg-red-50/30 text-center relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#A43032]"></div>
              <h3 className="font-heading text-2xl font-semibold text-[#A43032] mb-2">Raguia</h3>
              <p className="text-sm text-stone-600">Le spécialiste documentaire FR</p>
            </div>
            <div className="p-8 text-center bg-stone-50">
              <h3 className="font-heading text-2xl font-semibold text-stone-600 mb-2">ChatGPT Ent.</h3>
              <p className="text-sm text-stone-500">Le géant généraliste US</p>
            </div>
          </div>

          <div className="divide-y divide-stone-100">
            <FeatureRow
              title="Souveraineté des données"
              raguia="Hébergement 100% France (SecNumCloud / HDS possible)"
              dust="Hébergement aux États-Unis (Cloud Act)"
            />
            <FeatureRow
              title="Spécialisation"
              raguia="Recherche documentaire précise (RAG) sur vos fichiers métiers"
              dust="Assistant généraliste, génération de texte"
            />
            <FeatureRow
              title="Mise en place"
              raguia="Immédiate, interface dédiée à votre entreprise"
              dust="Déploiement lourd, formation des employés nécessaire"
            />
            <FeatureRow
              title="Support client"
              raguia="Équipe basée en France, accompagnement dédié"
              dust="Support globalisé, tickets en anglais"
            />

            <FeatureRow
              title="Abonnement mensuel"
              raguia={<>À partir de <span className="font-mono font-bold">90 €</span> / mois (forfait fixe pour l&rsquo;entreprise)</>}
              dust="Sur devis (très onéreux, minimum 150+ licences requis)"
            />
          </div>
        </div>
      </section>

      {/* Key Advantages */}
      <section className="py-16 px-6 lg:px-8 max-w-7xl mx-auto">
        <h2 className="font-heading text-3xl font-semibold text-center mb-12">Pourquoi choisir Raguia plutôt que ChatGPT Enterprise ?</h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
            <div className="size-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
              <Shield className="size-6 text-[#A43032]" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-3">Souveraineté & Sécurité</h3>
            <p className="text-stone-600">
              Vos documents RH, juridiques ou financiers ne doivent pas être soumis aux lois extraterritoriales américaines. Avec Raguia, vos données restent en France, garantissant une conformité stricte au RGPD.
            </p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
            <div className="size-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
              <Zap className="size-6 text-[#A43032]" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-3">Spécialisé pour vos documents</h3>
            <p className="text-stone-600">
              Contrairement à ChatGPT qui est un outil conversationnel général, Raguia est pensé dès le départ comme un moteur de recherche documentaire augmenté (RAG). Il cite ses sources et évite les hallucinations.
            </p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
            <div className="size-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
              <Globe className="size-6 text-[#A43032]" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-3">Budget maîtrisé</h3>
            <p className="text-stone-600">
              ChatGPT Enterprise impose souvent un minimum de licences très élevé. Raguia propose un modèle de facturation transparent, pensé pour la réalité économique des PME françaises.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto bg-[#010101] rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 size-64 bg-[#A43032] rounded-full blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>

          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6 relative z-10">
            Prêt à reprendre le contrôle de votre IA ?
          </h2>
          <p className="text-stone-400 text-lg mb-10 max-w-2xl mx-auto relative z-10">
            Testez Raguia et découvrez une solution d'IA souveraine, abordable et sécurisée pour votre entreprise.
          </p>
          <div className="relative z-10">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#A43032] text-white rounded-xl font-semibold hover:bg-red-600 transition-colors"
            >
              Demander une démo <ArrowRight className="size-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-6 text-center max-w-4xl mx-auto border-t border-stone-200">
        <p className="text-xs text-stone-400">
          Ce comparatif est basé sur les informations publiques disponibles à la date de sa rédaction. ChatGPT Enterprise est une marque déposée par OpenAI. Raguia n'est pas affilié à OpenAI.
        </p>
      </section>
    </div>
  );
}

function FeatureRow({ title, raguia, dust }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 hover:bg-stone-50/50 transition-colors">
      <div className="p-4 md:p-6 text-sm font-semibold text-stone-900 border-b md:border-b-0 md:border-r border-stone-100 flex items-center bg-stone-50 md:bg-transparent">
        {title}
      </div>
      <div className="p-4 md:p-6 text-sm text-stone-700 flex items-center justify-center text-center border-b md:border-b-0 md:border-r border-stone-100 bg-white">
        {raguia}
      </div>
      <div className="p-4 md:p-6 text-sm text-stone-500 flex items-center justify-center text-center bg-white">
        {dust}
      </div>
    </div>
  );
}
